import React, { ReactElement, useEffect, useMemo, useState } from "react";
import { useLocalization } from '@progress/kendo-react-intl';
import { enMessages } from "../../../../messages/en-US";
import moment from "moment";
import { SortDescriptor, process, State } from "@progress/kendo-data-query";
import { ExcelExport, ExcelExportColumn } from "@progress/kendo-react-excel-export";
import { Grid, GridColumn as Column, GridToolbar} from "@progress/kendo-react-grid";
import { Button } from "@progress/kendo-react-buttons";
import { CustomTextFilterCell } from "../../../Shared/Components/Table/CustomTextFilterCell";
import { Tooltip } from "@progress/kendo-react-tooltip";
import { downloadIcon } from "@progress/kendo-svg-icons";
import { DeviceType } from "../../../../Enums/DeviceType";

interface Props {
  events: any[];
  aggregator?: string;
  endpointSerialNo?: string;
  chartParentHeight?: number;
  deviceFeatures?: any;
  deviceType?: string;
}

interface Page {
  skip: number;
  take: number;
}

const page: Page = { skip: 0, take: 30 };

export default function DiagnosticsTable(props: Props): ReactElement {
  const localizationService = useLocalization();
  const initialSort: Array<SortDescriptor> = [{ field: "timestamp", dir: "desc" }];
  const [sort] = React.useState(initialSort);
  const _export = React.useRef<ExcelExport | null>(null);

  const tableHeight = props.chartParentHeight ? props.chartParentHeight + 60 : 600;
  
  const excelExport = () => {
    if (_export.current !== null) {
      _export.current.save();
    }
  };

  //Creating dataState to prevent issues with virtual scrolling
  const createAppState = (dataState: State) => {
    return {
      result: process(props.events || [], dataState),
      dataState: dataState,
    };
  };

  const [dataState, setDataState] = useState<any>(
    createAppState({
      skip: page.skip,
      take: page.take,
      sort: sort
    })
  );
  
  const dataStateChange = (event) => {
    setDataState(createAppState(event.dataState));
  };

   useEffect(() => {
    setDataState( createAppState({
      skip: page.skip,
      take: page.take,
      sort: sort
    }));
  }, [props]);

  // Helper function to check if value is empty
  const isEmptyValue = (value: any): boolean => {
    return value === null || value === undefined || value === '' || (typeof value === 'number' && isNaN(value));
  };

  //Formating Cells
  const OccurredCell = (props: any) => {
    const timestamp = props.dataItem.timestamp;
    const displayValue = isEmptyValue(timestamp) ? '-' : moment(timestamp).format('DD/MM/YYYY @ HH:mm:ss');
    
    return (
      <td>
        <div>{displayValue}</div>
      </td>
    );
  }

  const TemperatureCell = (props: any) => {
    const value = props.dataItem.temperature;
    const displayValue = isEmptyValue(value) ? '-' : typeof value === 'number' ? value.toFixed(2) : value;
    
    return (
      <td>
        <div>{displayValue}</div>
      </td>
    );
  }

  const DiagnosticTemperatureCell = (props: any) => {
    const value = props.dataItem.diagnosticTemperature;
    const displayValue = isEmptyValue(value) ? '-' : typeof value === 'number' ? value.toFixed(2) : value;
    
    return (
      <td>
        <div>{displayValue}</div>
      </td>
    );
  }

  const VoltageCell = (props: any) => {
    const value = props.dataItem.voltage;
    const displayValue = isEmptyValue(value) ? '-' : typeof value === 'number' ? value.toFixed(2) : value;
    
    return (
      <td>
        <div>{displayValue}</div>
      </td>
    );
  }

  const RssiCell = (props: any) => {
    const value = props.dataItem.rssi;
    const displayValue = isEmptyValue(value) ? '-' : typeof value === 'number' ? value.toFixed(2) : value;
    
    return (
      <td>
        <div>{displayValue}</div>
      </td>
    );
  }

  const EndpointRssiCell = (props: any) => {
    const value = props.dataItem.endpointRssi;
    const displayValue = isEmptyValue(value) ? '-' : typeof value === 'number' ? value.toFixed(2) : value;
    
    return (
      <td>
        <div>{displayValue}</div>
      </td>
    );
  }

  const CumulativeCell = (props: any) => {
    const value = props.dataItem.cumulative;
    const displayValue = isEmptyValue(value) ? '-' : typeof value === 'number' ? value.toFixed(2) : value;
    
    return (
      <td>
        <div>{displayValue}</div>
      </td>
    );
  }

  const VolumeDispensedCell = (props: any) => {
    const value = props.dataItem.volumeDispensed;
    const displayValue = isEmptyValue(value) ? '-' : typeof value === 'number' ? value.toFixed(2) : value;
    
    return (
      <td>
        <div>{displayValue}</div>
      </td>
    );
  }

  const PressureMaxCell = (props: any) => {
    const value = props.dataItem.pressureMax;
    const displayValue = isEmptyValue(value) ? '-' : typeof value === 'number' ? value.toFixed(2) : value;
    
    return (
      <td>
        <div>{displayValue}</div>
      </td>
    );
  }

  const PressureMeanCell = (props: any) => {
    const value = props.dataItem.pressureMean;
    const displayValue = isEmptyValue(value) ? '-' : typeof value === 'number' ? value.toFixed(2) : value;
    
    return (
      <td>
        <div>{displayValue}</div>
      </td>
    );
  }

  const PressureMinCell = (props: any) => {
    const value = props.dataItem.pressureMin;
    const displayValue = isEmptyValue(value) ? '-' : typeof value === 'number' ? value.toFixed(2) : value;
    
    return (
      <td>
        <div>{displayValue}</div>
      </td>
    );
  }

  const ButtonCell = (props: any) => {
    const value = props.dataItem.button;
    const displayValue = isEmptyValue(value) ? '-' : typeof value === 'number' ? value.toFixed(2) : value;

    return (
      <td>
        <div>{displayValue}</div>
      </td>
    );
  }

  const BatteryCell = (props: any) => {
    const value = props.dataItem.coulombCount;
    const displayValue = isEmptyValue(value) ? '-' : typeof value === 'number' ? value.toFixed(2) : value;

    return (
      <td>
        <div>{displayValue}</div>
      </td>
    );
  }

  const SlotCell = (props: any) => {
    const value = props.dataItem.slot;
    const displayValue = isEmptyValue(value) ? '-' : value;

    return (
      <td>
        <div>{displayValue}</div>
      </td>
    );
  }

  const exportData = useMemo(() => {
    return (props.events || []).map((item) => ({
      ...item,
      timestampExport: item.timestamp ? moment(item.timestamp).format('DD/MM/YYYY HH:mm:ss') : '-',
      syncedExport: item.synced ? moment(item.synced).format('DD/MM/YYYY HH:mm:ss') : '-',
      temperatureExport: isEmptyValue(item.temperature) ? '-' : typeof item.temperature === 'number' ? item.temperature.toFixed(2) : item.temperature,
      diagnosticTemperatureExport: isEmptyValue(item.diagnosticTemperature) ? '-' : typeof item.diagnosticTemperature === 'number' ? item.diagnosticTemperature.toFixed(2) : item.diagnosticTemperature,
      voltageExport: isEmptyValue(item.voltage) ? '-' : typeof item.voltage === 'number' ? item.voltage.toFixed(2) : item.voltage,
      rssiExport: isEmptyValue(item.rssi) ? '-' : typeof item.rssi === 'number' ? item.rssi.toFixed(2) : item.rssi,
      endpointRssiExport: isEmptyValue(item.endpointRssi) ? '-' : typeof item.endpointRssi === 'number' ? item.endpointRssi.toFixed(2) : item.endpointRssi,
      cumulativeExport: isEmptyValue(item.cumulative) ? '-' : typeof item.cumulative === 'number' ? item.cumulative.toFixed(2) : item.cumulative,
      volumeDispensedExport: isEmptyValue(item.volumeDispensed) ? '-' : typeof item.volumeDispensed === 'number' ? item.volumeDispensed.toFixed(2) : item.volumeDispensed,
      pressureMaxExport: isEmptyValue(item.pressureMax) ? '-' : typeof item.pressureMax === 'number' ? item.pressureMax.toFixed(2) : item.pressureMax,
      pressureMeanExport: isEmptyValue(item.pressureMean) ? '-' : typeof item.pressureMean === 'number' ? item.pressureMean.toFixed(2) : item.pressureMean,
      pressureMinExport: isEmptyValue(item.pressureMin) ? '-' : typeof item.pressureMin === 'number' ? item.pressureMin.toFixed(2) : item.pressureMin,
      buttonExport: isEmptyValue(item.button) ? '-' : typeof item.button === 'number' ? item.button.toFixed(2) : item.button,
      coulombCountExport: isEmptyValue(item.coulombCount) ? '-' : typeof item.coulombCount === 'number' ? item.coulombCount.toFixed(2) : item.coulombCount,
      slotExport: isEmptyValue(item.slot) ? '-' : item.slot,
    }));
  }, [props.events]);

  const getExcelExportData = () => {
    return process(exportData, {filter: dataState.dataState.filter, sort: dataState.dataState.sort}).data;
  };

  return (
    <div>
      <ExcelExport data={getExcelExportData()} ref={_export} collapsible={true} fileName={`All Events - ${props.endpointSerialNo || 'Endpoint'}.xlsx`}>
        <ExcelExportColumn field="timestampExport" title={`${localizationService.toLanguageString('custom.occurred', enMessages.custom.occurred)}`} width={150}/>
        <ExcelExportColumn field="temperatureExport" title={`${localizationService.toLanguageString('custom.temperatureInC', enMessages.custom.temperatureInC)}`} />
        <ExcelExportColumn field="diagnosticTemperatureExport" title={localizationService.toLanguageString('custom.diagnosticTemperature', enMessages.custom.diagnosticsTemperature)} />
        <ExcelExportColumn field="voltageExport" title={`${localizationService.toLanguageString('custom.voltage', enMessages.custom.voltage)}`} />
        <ExcelExportColumn field="rssiExport" title={`${localizationService.toLanguageString('custom.rssiDbm', enMessages.custom.rssiDbm)}`}/>
        <ExcelExportColumn field="endpointRssiExport" title={`${localizationService.toLanguageString('custom.endpointRssi', enMessages.custom.endpointRssi)}`}/>
        {props.deviceType !== DeviceType.EndpointBF && props.deviceType !== DeviceType.EndpointMP2 && <ExcelExportColumn field="cumulativeExport" title={`${localizationService.toLanguageString('custom.cumulative', enMessages.custom.cumulative)}`}/>}
        <ExcelExportColumn field="volumeDispensedExport" title={localizationService.toLanguageString('custom.volumeDispensed', enMessages.custom.volumeDispensed)}/>
        <ExcelExportColumn field="pressureMaxExport" title={localizationService.toLanguageString('custom.pressureMax', enMessages.custom.pressureMax)}/>
        <ExcelExportColumn field="pressureMeanExport" title={localizationService.toLanguageString('custom.pressureMean', enMessages.custom.pressureMean)}/>
        <ExcelExportColumn field="pressureMinExport" title={localizationService.toLanguageString('custom.pressureMin', enMessages.custom.pressureMin)}/>
        <ExcelExportColumn field="buttonExport" title={localizationService.toLanguageString('custom.pressedTime', enMessages.custom.pressedTime)}/>
        <ExcelExportColumn field="coulombCountExport" title={`${localizationService.toLanguageString('custom.battery', enMessages.custom.battery)}`}/>
        <ExcelExportColumn field="slotExport" title={`${localizationService.toLanguageString('custom.slot', enMessages.custom.slot)}`}/>
      </ExcelExport>
      <Grid
        key={(props.events || []).length.toString()}
        rowHeight={46}
        take={page.take}
        data={dataState.result}
        scrollable={"virtual"}
        filterable={true}
        onDataStateChange={dataStateChange}
        {...dataState.dataState}
        sortable={true}
        style={{
          height: `${tableHeight}px`,
        }}
        className=" w-full z-0 extra-filter-button "
      >
        <GridToolbar>  
        <div style={{width:'100%', display:'flex', justifyContent:'flex-end'}}>
              <Tooltip anchorElement="target" position="bottom">
                <Button
                  title={localizationService.toLanguageString('custom.exportToExcel', enMessages.custom.exportToExcel)}
                  svgIcon={downloadIcon}
                  disabled={dataState.result.data.length === 0}
                  onClick={excelExport}
                />
              </Tooltip>
          </div>
        </GridToolbar>
        <Column field="timestamp" title={`${localizationService.toLanguageString('custom.occurred', enMessages.custom.occurred)}`} cell={OccurredCell} filterable={false}/>
        {props.deviceFeatures?.isTemperatureSupported && <Column field="temperature" title={`${localizationService.toLanguageString('custom.temperatureInC', enMessages.custom.temperatureInC)}`} cell={TemperatureCell} filterCell={CustomTextFilterCell}/>}
        <Column field="diagnosticTemperature" title={localizationService.toLanguageString('custom.diagnosticTemperature', enMessages.custom.diagnosticsTemperature)} cell={DiagnosticTemperatureCell} filterCell={CustomTextFilterCell}/>
        <Column field="voltage" title={`${localizationService.toLanguageString('custom.voltage', enMessages.custom.voltage)}`} cell={VoltageCell} filterCell={CustomTextFilterCell}/>
        <Column field="rssi" title={`${localizationService.toLanguageString('custom.rssiDbm', enMessages.custom.rssiDbm)}`} cell={RssiCell} filterCell={CustomTextFilterCell}/>
        <Column field="endpointRssi" title={`${localizationService.toLanguageString('custom.endpointRssi', enMessages.custom.endpointRssi)}`} cell={EndpointRssiCell} filterCell={CustomTextFilterCell}/>
        {props.deviceType !== DeviceType.EndpointBF && props.deviceType !== DeviceType.EndpointMP2 && <Column field="cumulative" title={`${localizationService.toLanguageString('custom.cumulative', enMessages.custom.cumulative)}`} cell={CumulativeCell} filterCell={CustomTextFilterCell}/>}
        {props.deviceFeatures?.isFlowSupported && <Column field="volumeDispensed" title={localizationService.toLanguageString('custom.volumeDispensed', enMessages.custom.volumeDispensed)} cell={VolumeDispensedCell} filterCell={CustomTextFilterCell}/>}
        {props.deviceFeatures?.isPressureSupported && <Column field="pressureMax" title={localizationService.toLanguageString('custom.pressureMax', enMessages.custom.pressureMax)} cell={PressureMaxCell} filterCell={CustomTextFilterCell}/>}
        {props.deviceFeatures?.isPressureSupported && <Column field="pressureMean" title={localizationService.toLanguageString('custom.pressureMean', enMessages.custom.pressureMean)} cell={PressureMeanCell} filterCell={CustomTextFilterCell}/>}
        {props.deviceFeatures?.isPressureSupported && <Column field="pressureMin" title={localizationService.toLanguageString('custom.pressureMin', enMessages.custom.pressureMin)} cell={PressureMinCell} filterCell={CustomTextFilterCell}/>}
        <Column field="button" title={localizationService.toLanguageString('custom.pressedTime', enMessages.custom.pressedTime)} cell={ButtonCell} filterCell={CustomTextFilterCell}/>
        <Column field="coulombCount" title={`${localizationService.toLanguageString('custom.battery', enMessages.custom.battery)}`} cell={BatteryCell} filterCell={CustomTextFilterCell}/>
        <Column field="slot" title={`${localizationService.toLanguageString('custom.slot', enMessages.custom.slot)}`} cell={SlotCell} filterCell={CustomTextFilterCell}/>
      </Grid>     
  </div>
  );
}